import type { WidenMediaElement } from '@hub/libs/widen-utils';
import { MediaElementType } from '@hub/libs/widen-utils';
import { useEffect, useRef, useState } from 'react';
import type { ActiveBreakpoints } from '@hub/hooks/useBreakpoints';
import { useBreakpoints } from '@hub/hooks/useBreakpoints';
import { useTranslation } from 'next-i18next';
import { useKeenSlider } from 'keen-slider/react';
import { useIsomorphicLayoutEffect } from '@hub/hooks/useIsomorphicLayoutEffect';
import classNames from 'classnames';
import styles from './ProductMediaCarousel.module.css';
import { WidenImage } from '@hub/common/Widen/WidenImage';
import { ProductMediaCarouselDisplayType } from '@hub/common/ProductMediaCarousel/ProductMediaModalCarousel';

export interface ProductMediaFadeCarouselProps {
  activeIndex: number;
  media: WidenMediaElement[];
  onChange: (idx: number) => void;
  onClick: (idx: number) => void;
  displayType?: ProductMediaCarouselDisplayType;
  size?: FadeCarouselSizeDefinition;
}

export interface FadeCarouselSizeDefinition {
  width: { [key in keyof Partial<ActiveBreakpoints>]: number };
  height: { [key in keyof Partial<ActiveBreakpoints>]: number };
}

const DefaultSize: FadeCarouselSizeDefinition = {
  width: {
    lg: 210,
    xl: 250,
  },
  height: {
    lg: 254,
    xl: 254,
  },
};
export const ProductMediaFadeCarousel = ({
  activeIndex,
  media,
  onChange,
  onClick,
  displayType,
  size,
}: ProductMediaFadeCarouselProps) => {
  const [dragging, setDragging] = useState(false);
  const isMountedRef = useRef<boolean>(false);
  const { lg, xl } = useBreakpoints();
  const { t } = useTranslation('product/productMediaCarousel');
  const [carouselRef, carousel] = useKeenSlider<HTMLDivElement>({
    initial: activeIndex,
    spacing: 0,
    loop: true,
    slides: media.length,
    slidesPerView: 1,
    slideChanged: s => {
      if (
        !isMountedRef.current ||
        Number.isNaN(s.details().relativeSlide) ||
        s.details().relativeSlide === activeIndex
      ) {
        return;
      }
      onChange(s.details().relativeSlide);
    },
    dragStart: () => {
      /* istanbul ignore if */
      if (!isMountedRef.current || !dragging) {
        return;
      }
      setDragging(true);
    },
    dragEnd: s => {
      /* istanbul ignore if */
      if (isMountedRef.current && dragging && Math.abs(s.details().speed) < 1) {
        setDragging(false);
      }
    },
    afterChange: () => {
      if (!isMountedRef.current || !dragging) {
        return;
      }
      setDragging(false);
    },
  });

  const getCarouselSize = () => {
    if (xl) {
      return {
        minWidth: size?.width.xl || DefaultSize.width.xl,
        minHeight: size?.height.xl || DefaultSize.height.xl,
      };
    }
    return {
      minWidth: size?.height.lg || DefaultSize.width.lg,
      minHeight: size?.height.lg || DefaultSize.height.lg,
    };
  };

  useEffect(() => {
    if (carousel) {
      carousel.resize();
    }
  }, [xl, lg, carousel]);

  useEffect(() => {
    if (carousel && !Number.isNaN(activeIndex) && carousel.details().relativeSlide !== activeIndex) {
      carousel.moveToSlideRelative(activeIndex, true);
    }
  }, [activeIndex, carousel]);

  useEffect(() => {
    if (carousel) {
      carousel.refresh();
    }
  }, [media, carousel]);

  useIsomorphicLayoutEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <>
      {/* when in full-width mode, set width to 100%. height cannot be a percentage value, therefore
       * set it to zero and instead use a paddingBottom of 67% (of the width) to keep the images aspect ratio correct */}
      <div
        data-testid="product-image-carousel-fade"
        ref={carouselRef}
        className={classNames('relative', 'max-w-full', 'max-h-full', styles.fadeCarousel)}
        style={{
          minWidth: getCarouselSize().minWidth,
          minHeight: getCarouselSize().minHeight,
          paddingBottom: displayType === ProductMediaCarouselDisplayType.FullWidth ? '67%' : 0,
        }}
      >
        {media.map((img, idx) => (
          <div
            className={classNames(
              'cursor-pointer relative transition-opacity duration-500',
              styles.fadeCarousel__element,
              activeIndex === idx ? 'opacity-100' : 'opacity-0'
            )}
            key={`fade-carousel-image-${img.shortCode}`}
            onClick={() => !dragging && onClick(idx)}
          >
            <WidenImage
              width={360}
              height={240}
              alt={img.description || img.title}
              shortCode={img.shortCode}
              filename={img.filename}
              layout="responsive"
              isPreviewImage
              isVideo={img.type === MediaElementType.VIDEO}
              title={img.title}
              priority={img.priority}
            />
            {img.marketingBadge && (
              <div className="absolute bottom-4 left-4">
                <WidenImage
                  shortCode={img.marketingBadge}
                  title={img.marketingBadge}
                  filename="pjcxxtsqjs"
                  width={60}
                  height={60}
                />
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="text-center text-xs my-3">
        {t('PRODUCT_MEDIA_CAROUSEL.CURRENT_INDEX', {
          active: activeIndex + 1,
          total: media.length,
          ns: 'bearing-hub/bearingHub',
        })}
      </div>
    </>
  );
};
